import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from './i18n/LanguageContext';

const SceneExplorer = ({ API, userToken, isDarkMode, showNotification, setCurrentView, onBack, hasPaidPlan }) => {
  const { t } = useTranslation();
  // App-shell caches: read synchronously so scenes + game settings + total
  // points render from frame 1 instead of the full-page loading message.
  const _readSeCache = (key) => {
    try { const c = localStorage.getItem(key); if (c) return JSON.parse(c); } catch {}
    return null;
  };
  const _cachedScenes = _readSeCache('seScenesCache');
  const _cachedTotalPoints = _readSeCache('seTotalPointsCache');
  const _cachedGameSettings = _readSeCache('seGameSettingsCache');
  const _cachedSavedCategoryOrder = _readSeCache('seSavedCategoryOrderCache');

  // Detect ?scene=<id> in URL — if present, render the scene view instantly
  // with cached data (preserves the user's place when refreshing inside a scene).
  const _wantedSceneIdFromUrl = (() => {
    try { return new URLSearchParams(window.location.search).get('scene') || null; } catch { return null; }
  })();
  const _cachedSceneData = _wantedSceneIdFromUrl ? _readSeCache(`seSceneDataCache_${_wantedSceneIdFromUrl}`) : null;

  const [scenes, setScenes] = useState(_cachedScenes || []);
  const [isPaid, setIsPaid] = useState(false);
  const [selectedScene, setSelectedScene] = useState(_cachedSceneData?.scene || null);
  const [sceneObjects, setSceneObjects] = useState(_cachedSceneData?.objects || []);
  const [availableLanguages, setAvailableLanguages] = useState(_cachedSceneData?.available_languages || []);
  const [displayLang, setDisplayLang] = useState('en');
  // mode='view' if URL has ?scene=, else 'browse'
  const [mode, setMode] = useState(_wantedSceneIdFromUrl ? 'view' : 'browse'); // browse | view | game_match | game_correct
  // Force spinner only when no cached scene data and URL says we should be inside one
  const _needsSceneFetch = !!_wantedSceneIdFromUrl && !_cachedSceneData;
  const [loading, setLoading] = useState((!_cachedScenes && !_cachedSceneData) || _needsSceneFetch);

  // Game state
  const [gameQueue, setGameQueue] = useState([]);
  const [currentGameIdx, setCurrentGameIdx] = useState(0);
  const [gameOptions, setGameOptions] = useState([]);
  const [gameScore, setGameScore] = useState(0);
  const [gameFinished, setGameFinished] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [highlightedDot, setHighlightedDot] = useState(null);
  // Game UI enhancements
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [scorePopup, setScorePopup] = useState(null);
  const [answerFlash, setAnswerFlash] = useState(null); // 'correct' | 'wrong' | null
  const [timerProgress, setTimerProgress] = useState(100);
  const [timerActive, setTimerActive] = useState(false);
  const [showTimesUp, setShowTimesUp] = useState(false);
  const timerRef = useRef(null);
  // "Is This Correct?" game state
  const [correctGameObj, setCorrectGameObj] = useState(null);
  const [correctGameLabel, setCorrectGameLabel] = useState('');
  const [correctGameIsRight, setCorrectGameIsRight] = useState(null);
  const [correctGameScore, setCorrectGameScore] = useState(0);
  const [correctGameIdx, setCorrectGameIdx] = useState(0);

  // Score & points state
  const [scoreResult, setScoreResult] = useState(null);
  const [totalPoints, setTotalPoints] = useState(_cachedTotalPoints || 0);
  const [lockedScene, setLockedScene] = useState(null); // for lock popup modal
  const [gameSettings, setGameSettings] = useState(_cachedGameSettings); // admin-configured game settings
  const [showJourney, setShowJourney] = useState(false); // My Journey modal
  const [resetConfirm, setResetConfirm] = useState(null); // { sceneId, sceneName, dependentScenes }
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [savedCategoryOrder, setSavedCategoryOrder] = useState(_cachedSavedCategoryOrder || []);

  // Zoom state for mobile
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const lastTouchRef = useRef(null);
  const lastDistRef = useRef(null);

  const headers = { 'Authorization': `Bearer ${userToken}` };

  const [languagesConfig, setLanguagesConfig] = useState([]);
  // Build langLabels dynamically from config
  const langLabels = {};
  languagesConfig.forEach(l => { langLabels[l.code] = l.name; });

  // Short display names for language switcher (no flags — clean text only)
  const langShort = {
    en: 'Eng', ar: 'AR', fr: 'FR', es: 'ES', pt: 'PT', de: 'DE', it: 'IT',
    zh: 'ZH', ja: 'JA', darija_latin: 'Darija', darija_script: 'عربي',
    tr: 'TR', nl: 'NL', ko: 'KO', hi: 'HI', ru: 'RU'
  };

  // Per-scene quiz config helper: scene-level > global > fallback
  const getNestedVal = (obj, path) => {
    if (!obj) return undefined;
    return path.split('.').reduce((o, k) => o?.[k], obj);
  };
  const getQuizSetting = (path, fallback) => {
    const sceneVal = getNestedVal(selectedScene?.quiz_config, path);
    if (sceneVal !== undefined && sceneVal !== null && sceneVal !== '') return sceneVal;
    const globalVal = getNestedVal(gameSettings, path);
    if (globalVal !== undefined && globalVal !== null && globalVal !== '') return globalVal;
    return fallback;
  };

  // Audio state
  const audioRef = useRef(null);

  const playAudio = (url) => {
    if (!url) return;
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    const resolved = resolveImageUrl(url);
    const audio = new Audio(resolved);
    audioRef.current = audio;
    audio.play().catch(e => console.error('Audio play failed:', e));
  };

  useEffect(() => { loadScenes(); }, []);

  // Auto-resume: if URL has ?scene=<id>, ensure we have the LATEST scene data.
  // Even when cache was used for instant first-paint, we refetch in background so
  // admin edits to scene text/images propagate immediately (previously this returned
  // early when cache was hit, which made admin updates invisible until cache cleared).
  useEffect(() => {
    if (scenes.length === 0) return;
    let wantedId;
    try { wantedId = new URLSearchParams(window.location.search).get('scene'); } catch { return; }
    if (!wantedId) return;
    const sc = scenes.find(s => String(s.id) === wantedId);
    if (!sc) return;
    // Always fetch fresh — refreshes any cached/already-loaded data with latest from backend.
    // openScene overwrites state when the response arrives, so this is safe to call even
    // when we're already viewing the scene.
    openScene(sc);
  }, [scenes]); // intentionally only watches scenes — openScene is stable

  const loadScenes = async () => {
    try {
      const res = await fetch(`${API}/scene-explorer/scenes`, { headers });
      if (res.ok) {
        const data = await res.json();
        const list = data.scenes || [];
        setScenes(list);
        try { localStorage.setItem('seScenesCache', JSON.stringify(list)); } catch {}
        setIsPaid(data.is_paid || false);
      }
    } catch (e) { console.error('Error loading scenes:', e); }
    finally { setLoading(false); }
    // Load total points
    try {
      const ptsRes = await fetch(`${API}/scene-explorer/total-points`, { headers });
      if (ptsRes.ok) {
        const ptsData = await ptsRes.json();
        const pts = ptsData.total_points || 0;
        setTotalPoints(pts);
        try { localStorage.setItem('seTotalPointsCache', JSON.stringify(pts)); } catch {}
      }
    } catch (e) { /* ignore */ }
  };

  const submitScore = async (score, total, gameType) => {
    if (!selectedScene?.id) return;
    try {
      const res = await fetch(`${API}/scene-explorer/scenes/${selectedScene.id}/submit-score`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ score, total, game_type: gameType })
      });
      if (res.ok) {
        const data = await res.json();
        setScoreResult(data);
        loadScenes(); // refresh progress bars + total points
      }
    } catch (e) { console.error('Error submitting score:', e); }
  };

  const openScene = async (scene) => {
    try {
      const res = await fetch(`${API}/scene-explorer/scenes/${scene.id}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSelectedScene(data.scene);
        setSceneObjects(data.objects || []);
        const langs = data.available_languages || ['en', 'darija_latin', 'darija_script'];
        setAvailableLanguages(langs);
        if (data.languages_config?.length) setLanguagesConfig(data.languages_config);
        setMode('view');
        setScale(1);
        setTranslate({ x: 0, y: 0 });
        // Cache + URL push so Ctrl+R returns directly to this scene
        try {
          localStorage.setItem(`seSceneDataCache_${scene.id}`, JSON.stringify({
            scene: data.scene, objects: data.objects || [], available_languages: langs
          }));
        } catch {}
        try { window.history.pushState({}, '', `/games/SceneExplorer?scene=${scene.id}`); } catch {}
      } else if (res.status === 403) {
        const errData = await res.json().catch(() => ({}));
        showNotification(errData.detail || t('scene_locked'), 'error');
      }
    } catch (e) { showNotification(t('scene_load_error'), 'error'); }
  };

  const goBack = () => {
    if (mode === 'view' || mode === 'game_match' || mode === 'game_correct') {
      setMode('browse');
      setSelectedScene(null);
      setSceneObjects([]);
      setGameFinished(false);
      try { window.history.pushState({}, '', '/games/SceneExplorer'); } catch {}
      setGameScore(0);
      setCurrentGameIdx(0);
      setScoreResult(null);
      setStreak(0);
      setBestStreak(0);
      stopTimer();
      setScale(1);
      setTranslate({ x: 0, y: 0 });
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    } else if (onBack) {
      onBack();
    } else if (setCurrentView) {
      setCurrentView('translator');
    }
  };

  // ── Zoom/Pan handlers for mobile ──
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      const dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
      lastDistRef.current = dist;
    } else if (e.touches.length === 1 && scale > 1) {
      lastTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  }, [scale]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
      if (lastDistRef.current) {
        const newScale = Math.min(3, Math.max(1, scale * (dist / lastDistRef.current)));
        setScale(newScale);
        if (newScale <= 1) setTranslate({ x: 0, y: 0 });
      }
      lastDistRef.current = dist;
    } else if (e.touches.length === 1 && scale > 1 && lastTouchRef.current) {
      const dx = e.touches[0].clientX - lastTouchRef.current.x;
      const dy = e.touches[0].clientY - lastTouchRef.current.y;
      setTranslate(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      lastTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  }, [scale]);

  const handleTouchEnd = useCallback(() => {
    lastDistRef.current = null;
    lastTouchRef.current = null;
  }, []);

  // ── Timer helpers ──
  const startTimer = () => {
    if (gameSettings?.ui?.show_timer_bar === false) return;
    stopTimer();
    const duration = (gameSettings?.ui?.timer_seconds || 15) * 1000;
    const interval = 50;
    let elapsed = 0;
    setTimerProgress(100);
    setTimerActive(true);
    timerRef.current = setInterval(() => {
      elapsed += interval;
      const pct = Math.max(0, 100 - (elapsed / duration) * 100);
      setTimerProgress(pct);
      if (pct <= 0) {
        stopTimer();
        setShowTimesUp(true);
      }
    }, interval);
  };
  const stopTimer = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setTimerActive(false);
  };

  // Answer feedback effects
  const showCorrectFeedback = () => {
    setAnswerFlash('correct');
    setTimeout(() => setAnswerFlash(null), 400);
    if (gameSettings?.ui?.show_score_popup !== false) {
      const pts = gameSettings?.points_per_correct ?? 10;
      setScorePopup(`+${pts}`);
      setTimeout(() => setScorePopup(null), 800);
    }
  };
  const showWrongFeedback = () => {
    setAnswerFlash('wrong');
    setTimeout(() => setAnswerFlash(null), 400);
  };

  // Handle timer expiration — auto-skip question
  useEffect(() => {
    if (!showTimesUp) return;
    setStreak(0);
    showWrongFeedback();
    const timeout = setTimeout(() => {
      setShowTimesUp(false);
      if (mode === 'game_match') {
        const nextIdx = currentGameIdx + 1;
        if (nextIdx >= gameQueue.length) {
          setGameFinished(true);
          submitScore(gameScore, gameQueue.length, 'match_the_label');
        } else {
          setCurrentGameIdx(nextIdx);
          setHighlightedDot(gameQueue[nextIdx]?.id || null);
          generateOptions(gameQueue, nextIdx);
          setSelectedAnswer(null);
          startTimer();
        }
      } else if (mode === 'game_correct') {
        const nextIdx = correctGameIdx + 1;
        if (nextIdx >= gameQueue.length) {
          const finalScore = correctGameScore;
          setGameFinished(true);
          setGameScore(finalScore);
          submitScore(finalScore, gameQueue.length, 'is_this_correct');
        } else {
          setCorrectGameIdx(nextIdx);
          setSelectedAnswer(null);
          pickCorrectGameQuestion(gameQueue, nextIdx);
          startTimer();
        }
      }
    }, 1500);
    return () => clearTimeout(timeout);
  }, [showTimesUp]);

  // ── Game: Match the Label ──
  const startMatchGame = () => {
    const includedIds = selectedScene?.quiz_config?.match_game?.included_objects;
    const pool = includedIds?.length ? sceneObjects.filter(o => includedIds.includes(o.id)) : sceneObjects;
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    setGameQueue(shuffled);
    setCurrentGameIdx(0);
    setGameScore(0);
    setGameFinished(false);
    setScoreResult(null);
    setSelectedAnswer(null);
    setStreak(0);
    setBestStreak(0);
    setHighlightedDot(shuffled[0]?.id || null);
    generateOptions(shuffled, 0);
    setMode('game_match');
    startTimer();
  };

  const generateOptions = (queue, idx) => {
    const correct = queue[idx];
    if (!correct) return;
    const gameLang = gameSettings?.game_language || 'darija_latin';
    const numOpts = Math.max(2, Math.min(6, getQuizSetting('match_game.num_options', 4)));
    const others = sceneObjects.filter(o => o.id !== correct.id).sort(() => Math.random() - 0.5).slice(0, numOpts - 1);
    const options = [...others, correct].sort(() => Math.random() - 0.5);
    setGameOptions(options.map(o => ({
      id: o.id,
      label: o.labels?.[gameLang] || o.labels?.en || '',
      isCorrect: o.id === correct.id
    })));
  };

  const handleMatchAnswer = (option) => {
    stopTimer();
    setSelectedAnswer(option.id);
    const isCorrect = option.isCorrect;
    if (isCorrect) {
      setGameScore(prev => prev + 1);
      setStreak(prev => { const n = prev + 1; setBestStreak(b => Math.max(b, n)); return n; });
      showCorrectFeedback();
    } else {
      setStreak(0);
      showWrongFeedback();
    }

    setTimeout(() => {
      const nextIdx = currentGameIdx + 1;
      if (nextIdx >= gameQueue.length) {
        const finalScore = gameScore + (isCorrect ? 1 : 0);
        setGameFinished(true);
        submitScore(finalScore, gameQueue.length, 'match_the_label');
      } else {
        setCurrentGameIdx(nextIdx);
        setHighlightedDot(gameQueue[nextIdx]?.id || null);
        generateOptions(gameQueue, nextIdx);
        setSelectedAnswer(null);
        startTimer();
      }
    }, 800);
  };

  // ── Game: Is This Correct? ──
  const startCorrectGame = () => {
    const includedIds = selectedScene?.quiz_config?.correct_game?.included_objects;
    const pool = includedIds?.length ? sceneObjects.filter(o => includedIds.includes(o.id)) : sceneObjects;
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    setGameQueue(shuffled);
    setCorrectGameIdx(0);
    setCorrectGameScore(0);
    setGameFinished(false);
    setScoreResult(null);
    setCorrectGameIsRight(null);
    setStreak(0);
    setBestStreak(0);
    pickCorrectGameQuestion(shuffled, 0);
    setMode('game_correct');
    startTimer();
  };

  const pickCorrectGameQuestion = (queue, idx) => {
    const obj = queue[idx];
    if (!obj) return;
    const gameLang = gameSettings?.game_language || 'darija_latin';
    setCorrectGameObj(obj);
    setHighlightedDot(obj.id);
    // 50% chance show correct, 50% show wrong
    const showCorrect = Math.random() > 0.5;
    if (showCorrect) {
      setCorrectGameLabel(obj.labels?.[gameLang] || obj.labels?.en || '');
      setCorrectGameIsRight(true);
    } else {
      const others = sceneObjects.filter(o => o.id !== obj.id);
      const wrong = others[Math.floor(Math.random() * others.length)];
      setCorrectGameLabel(wrong?.labels?.[gameLang] || wrong?.labels?.en || '??');
      setCorrectGameIsRight(false);
    }
  };

  const handleCorrectAnswer = (userSaysYes) => {
    stopTimer();
    const isCorrect = (userSaysYes && correctGameIsRight) || (!userSaysYes && !correctGameIsRight);
    if (isCorrect) {
      setCorrectGameScore(prev => prev + 1);
      setStreak(prev => { const n = prev + 1; setBestStreak(b => Math.max(b, n)); return n; });
      showCorrectFeedback();
    } else {
      setStreak(0);
      showWrongFeedback();
    }

    setSelectedAnswer(isCorrect ? 'right' : 'wrong');
    setTimeout(() => {
      const nextIdx = correctGameIdx + 1;
      if (nextIdx >= gameQueue.length) {
        const finalScore = correctGameScore + (isCorrect ? 1 : 0);
        setGameFinished(true);
        setGameScore(finalScore);
        submitScore(finalScore, gameQueue.length, 'is_this_correct');
      } else {
        setCorrectGameIdx(nextIdx);
        setSelectedAnswer(null);
        pickCorrectGameQuestion(gameQueue, nextIdx);
        startTimer();
      }
    }, 800);
  };

  // Resolve image URL — handles local paths (/api/static/...) and full URLs (Dropbox etc.)
  const resolveImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url.replace('?dl=0', '?raw=1').replace('www.dropbox.com', 'dl.dropboxusercontent.com');
    }
    return `${API.replace('/api', '')}${url}`;
  };

  const cardClass = isDarkMode ? 'bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-700' : 'bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-sm';

  // Category icons for visual flair
  const categoryIcons = {
    'Home': '🏠', 'Kitchen': '🍳', 'Living Room': '🛋️', 'Bedroom': '🛏️', 'Bathroom': '🚿',
    'Outside': '🌳', 'Nature': '🏞️', 'Market': '🏪', 'School': '🏫', 'Restaurant': '🍽️',
    'Street': '🛣️', 'Transport': '🚗', 'Beach': '🏖️', 'Garden': '🌻', 'Office': '💼',
    'River': '🏞️', 'Mountain': '⛰️', 'City': '🏙️', 'Farm': '🌾', 'Hospital': '🏥'
  };

  // Group scenes by category (all scenes, for category pills counts)
  const allGrouped = scenes.reduce((acc, scene) => {
    const cat = scene.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(scene);
    return acc;
  }, {});
  // Use admin-defined category order; any unsaved categories go at the end, 'Other' always last
  const categoryOrder = Object.keys(allGrouped).sort((a, b) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    const idxA = savedCategoryOrder.indexOf(a);
    const idxB = savedCategoryOrder.indexOf(b);
    if (idxA >= 0 && idxB >= 0) return idxA - idxB;
    if (idxA >= 0) return -1;
    if (idxB >= 0) return 1;
    return a.localeCompare(b);
  });

  // Filter scenes by search + selected category
  const query = searchQuery.toLowerCase().trim();
  const filteredScenes = scenes.filter(scene => {
    // Category filter
    if (selectedCategory !== 'All' && (scene.category || 'Other') !== selectedCategory) return false;
    // Search filter
    if (query) {
      const name = (scene.name || '').toLowerCase();
      const nameDarija = (scene.name_darija_latin || '').toLowerCase();
      const nameAr = scene.name_ar || '';
      const cat = (scene.category || '').toLowerCase();
      if (!name.includes(query) && !nameDarija.includes(query) && !nameAr.includes(query) && !cat.includes(query)) return false;
    }
    return true;
  });

  // Group filtered scenes for display
  const groupedScenes = filteredScenes.reduce((acc, scene) => {
    const cat = scene.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(scene);
    return acc;
  }, {});
  const filteredCategoryOrder = Object.keys(groupedScenes).sort((a, b) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    const idxA = savedCategoryOrder.indexOf(a);
    const idxB = savedCategoryOrder.indexOf(b);
    if (idxA >= 0 && idxB >= 0) return idxA - idxB;
    if (idxA >= 0) return -1;
    if (idxB >= 0) return 1;
    return a.localeCompare(b);
  });

  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [howToPlaySteps, setHowToPlaySteps] = useState([
    { title: 'Pick a scene', description: 'Choose a scene like Kitchen, Bedroom, or a nature scene like River' },
    { title: 'Explore the image', description: 'See labeled dots on the image — switch between English, Darija, Arabic, French & more' },
    { title: 'Play the games', description: '"Match the Label" — pick the right word. "Is This Correct?" — decide if the label is right or wrong' }
  ]);

  // Load how-to-play from settings
  useEffect(() => {
    const loadHowToPlay = async () => {
      try {
        const res = await fetch(`${API}/scene-explorer/settings`);
        if (res.ok) {
          const data = await res.json();
          if (data.how_to_play?.length) setHowToPlaySteps(data.how_to_play);
          if (data.languages?.length) setLanguagesConfig(data.languages);
          if (data.categories?.length) {
            setSavedCategoryOrder(data.categories);
            try { localStorage.setItem('seSavedCategoryOrderCache', JSON.stringify(data.categories)); } catch {}
          }
          if (data.game_settings) {
            setGameSettings(data.game_settings);
            try { localStorage.setItem('seGameSettingsCache', JSON.stringify(data.game_settings)); } catch {}
          }
        }
      } catch (e) { /* use defaults */ }
    };
    loadHowToPlay();
  }, [API]);

  // ── Browse Mode ──
  const renderBrowse = () => (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        {(onBack || setCurrentView) && (
          <button onClick={goBack} className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        <div className="flex-1">
          <h1 className={`text-2xl font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('scene_explorer_title')}</h1>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('scene_explorer_subtitle')}</p>
        </div>
        {totalPoints > 0 && (
          <div className={`flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs sm:text-sm font-bold shrink-0 ${
            isDarkMode ? 'bg-amber-900/40 text-amber-300 border border-amber-700/40' : 'bg-amber-50 text-amber-700 border border-amber-200'
          }`}>
            <span>⭐</span> {totalPoints} pts
          </div>
        )}
      </div>

      {/* How to Play + My Journey buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setShowHowToPlay(true)}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
            isDarkMode ? 'bg-purple-900/40 text-purple-300 hover:bg-purple-900/60 border border-purple-700/40' : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200'
          }`}>
          <span>🎮</span> {t('scene_how_to_play')}
        </button>
        <button onClick={() => setShowJourney(true)}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
            isDarkMode ? 'bg-amber-900/40 text-amber-300 hover:bg-amber-900/60 border border-amber-700/40' : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
          }`}>
          <span>{gameSettings?.ui?.journey_button_icon || '🗺️'}</span> {t('scene_my_journey')}
        </button>
      </div>

      {/* Overall Progress Card */}
      {scenes.length > 0 && (() => {
        const totalScenes = scenes.length;
        const completedScenes = scenes.filter(s => s.user_progress?.completed).length;
        const startedScenes = scenes.filter(s => s.user_progress && !s.user_progress.completed).length;
        const overallPct = totalScenes > 0 ? Math.round((completedScenes / totalScenes) * 100) : 0;
        return (
          <div className={`${cardClass} p-4`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-lg">{gameSettings?.ui?.progress_icon || '📊'}</span>
                <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('scene_your_progress')}</span>
              </div>
              <div className="flex items-center gap-3 text-[11px] font-semibold">
                <span className={isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}>{completedScenes} {t('scene_completed_label')}</span>
                {startedScenes > 0 && <span className={isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}>{startedScenes} {t('scene_in_progress_label')}</span>}
                <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>{totalScenes} {t('scene_total_label')}</span>
              </div>
            </div>
            <div className={`w-full h-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all"
                style={{ width: `${overallPct}%` }} />
            </div>
            {totalPoints > 0 && (
              <p className={`text-[11px] mt-1.5 font-semibold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                {t('scene_total_label_prefix')} {totalPoints} {t('scene_total_points_earned')}
              </p>
            )}
          </div>
        );
      })()}

      {/* Search bar */}
      {scenes.length > 0 && (
        <div className="relative">
          <svg className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={t('scene_explorer_search')}
            className={`w-full pl-10 pr-10 py-2.5 rounded-xl text-sm font-medium outline-none transition-all ${
              isDarkMode
                ? 'bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:border-purple-500'
                : 'bg-white border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-purple-400 shadow-sm'
            }`}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')}
              className={`absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Category pills */}
      {scenes.length > 0 && categoryOrder.length > 1 && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setSelectedCategory('All')}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
              selectedCategory === 'All'
                ? 'bg-purple-600 text-white shadow-sm'
                : isDarkMode ? 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300 shadow-sm'
            }`}>
            All ({scenes.length})
          </button>
          {categoryOrder.map(cat => (
            <button key={cat} onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all flex items-center gap-1 ${
                selectedCategory === cat
                  ? 'bg-purple-600 text-white shadow-sm'
                  : isDarkMode ? 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300 shadow-sm'
              }`}>
              <span className="text-sm">{categoryIcons[cat] || '📍'}</span> {cat} ({allGrouped[cat].length})
            </button>
          ))}
        </div>
      )}

      {/* Scenes grouped by category */}
      {scenes.length === 0 ? (
        <div className={`${cardClass} p-12 text-center`}>
          <p className="text-4xl mb-3">🖼️</p>
          <p className={`font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{t('scene_explorer_no_scenes_yet')}</p>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('scene_explorer_check_back')}</p>
        </div>
      ) : filteredScenes.length === 0 ? (
        <div className={`${cardClass} p-12 text-center`}>
          <p className="text-4xl mb-3">🔍</p>
          <p className={`font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{t('scene_explorer_no_scenes_found')}</p>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {t('scene_try_different_search')}{selectedCategory !== 'All' ? ` ${t('scene_or_category')}` : ''}
          </p>
        </div>
      ) : (
        filteredCategoryOrder.map(cat => (
          <div key={cat} className={`rounded-2xl overflow-hidden ${
            isDarkMode
              ? 'bg-gray-800/60 border border-gray-700/50'
              : 'bg-white border border-gray-200 shadow-sm'
          }`}>
            {/* Category header */}
            {selectedCategory === 'All' && (
              <div className={`px-5 py-3.5 flex items-center justify-between ${
                isDarkMode ? 'bg-gray-800/80' : 'bg-gray-50/80'
              }`}>
                <div className="flex items-center gap-2.5">
                  <span className="text-xl">{categoryIcons[cat] || '📍'}</span>
                  <h2 className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{cat}</h2>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${
                  isDarkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-50 text-purple-600'
                }`}>
                  {groupedScenes[cat].length} scene{groupedScenes[cat].length !== 1 ? 's' : ''}
                </span>
              </div>
            )}
            {/* Divider */}
            {selectedCategory === 'All' && (
              <div className={isDarkMode ? 'border-t border-gray-700/50' : 'border-t border-gray-100'} />
            )}

            {/* Scene cards in this category */}
            <div className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {groupedScenes[cat].map(scene => {
                  const isLocked = scene.is_locked && !scene.is_unlocked;
                  const pct = scene.user_progress
                    ? Math.round(((scene.user_progress.best_score || 0) / (scene.object_count || 1)) * 100)
                    : 0;
                  const isComplete = scene.user_progress?.completed;
                  return (
                  <button key={scene.id} onClick={() => {
                      if (isLocked) { setLockedScene(scene); return; }
                      openScene(scene);
                    }}
                    className={`rounded-xl overflow-hidden text-left transition-all hover:scale-[1.02] active:scale-[0.98] group relative ${
                      isDarkMode
                        ? 'bg-gray-700/50 border border-gray-600/40'
                        : 'bg-white border border-gray-200 shadow-sm'
                    }`}>
                    {/* Scene Image */}
                    <div className="relative h-36 md:h-44 overflow-hidden">
                      {scene.image_url ? (
                        <img src={resolveImageUrl(scene.image_url)} alt={scene.name}
                          className={`w-full h-full object-cover transition-transform group-hover:scale-105 ${isLocked ? 'grayscale opacity-50' : ''}`} />
                      ) : (
                        <div className={`w-full h-full flex items-center justify-center ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                          <span className={`text-4xl ${isLocked ? 'opacity-40' : ''}`}>{categoryIcons[cat] || '🖼️'}</span>
                        </div>
                      )}
                      {/* Gradient overlay at bottom for text readability */}
                      <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/70 to-transparent" />
                      {/* Lock overlay */}
                      {isLocked && (
                        <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-1 z-10">
                          <svg className="w-7 h-7 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          {scene.unlock_required_names?.length > 0 && (
                            <p className="text-[9px] text-white/70 text-center px-2 leading-tight">
                              {t('scene_complete_prefix')} {scene.unlock_required_names.join(' & ')}
                            </p>
                          )}
                        </div>
                      )}
                      {/* Top-right badges */}
                      <div className="absolute top-2 right-2 flex flex-col items-end gap-1 z-10">
                        {scene.is_premium && !isLocked && (
                          <span className="text-[9px] font-bold px-2 py-0.5 bg-amber-500/90 text-white rounded-full shadow-sm">PRO</span>
                        )}
                        {isComplete && (
                          <span className="text-[9px] font-bold px-2 py-0.5 bg-emerald-500/90 text-white rounded-full shadow-sm flex items-center gap-0.5">
                            <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>{t('scene_done_badge')}
                          </span>
                        )}
                        {scene.is_locked && scene.is_unlocked && !isComplete && (
                          <span className="text-[9px] font-bold px-2 py-0.5 bg-emerald-500/90 text-white rounded-full shadow-sm flex items-center gap-0.5">
                            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                            </svg>{t('scene_unlocked_badge')}
                          </span>
                        )}
                      </div>
                      {/* Title overlay on image bottom */}
                      <div className="absolute bottom-0 inset-x-0 p-3 z-10">
                        <p className="font-bold text-sm text-white leading-tight drop-shadow-md">{scene.name}</p>
                        {(scene.name_darija_latin || scene.name_ar) && (
                          <p className="text-[11px] text-white/80 mt-0.5 drop-shadow-sm">
                            {scene.name_darija_latin || ''}{scene.name_darija_latin && scene.name_ar ? ' · ' : ''}{scene.name_ar || ''}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Bottom info bar */}
                    <div className="px-3 py-2.5">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className={`text-[10px] font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {scene.object_count || 0} words
                        </span>
                        <span className={`text-[10px] font-bold ${
                          isComplete ? (isDarkMode ? 'text-emerald-400' : 'text-emerald-600')
                            : pct > 0 ? (isDarkMode ? 'text-yellow-400' : 'text-yellow-600')
                            : (isDarkMode ? 'text-gray-500' : 'text-gray-400')
                        }`}>
                          {isComplete ? 'Completed' : pct > 0 ? `${pct}%` : 'New'}
                        </span>
                      </div>
                      {/* Progress bar */}
                      <div className={`w-full h-1 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                        <div className={`h-full rounded-full transition-all ${
                          isComplete ? 'bg-gradient-to-r from-emerald-500 to-teal-400'
                            : pct > 0 ? 'bg-gradient-to-r from-yellow-500 to-orange-400'
                            : ''
                        }`}
                          style={{ width: `${Math.min(100, pct)}%` }} />
                      </div>
                    </div>
                  </button>
                );
                })}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  // ── View Mode (scene with labels) ──
  const renderView = () => (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={goBack} className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedScene?.name}</h2>
        </div>
      </div>

      {/* Language Toggle — centered, wrapping pills */}
      <div className="flex flex-wrap justify-center gap-1">
        {availableLanguages.map(lang => (
          <button key={lang} onClick={() => setDisplayLang(lang)}
            className={`px-2 py-0.5 rounded-full text-[10px] font-bold transition-all ${
              displayLang === lang
                ? 'bg-purple-600 text-white shadow-sm shadow-purple-500/20'
                : isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {langShort[lang] || lang.toUpperCase().slice(0, 3)}
          </button>
        ))}
      </div>

      {/* Scene Image with Labels */}
      <div className={`${cardClass} p-2 overflow-hidden`}
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}>
        <div className="relative" style={{ transform: `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`, transformOrigin: 'center center', transition: 'transform 0.1s ease-out' }}>
          <img src={resolveImageUrl(selectedScene?.image_url)} alt={selectedScene?.name}
            className="w-full rounded-lg" draggable={false} />
          {sceneObjects.map(obj => {
            const label = obj.labels?.[displayLang] || obj.labels?.en || '';
            const langConf = languagesConfig.find(l => l.code === displayLang);
            const isRtl = langConf?.dir === 'rtl';
            const audioUrl = obj.audio_urls?.[displayLang] || '';
            return (
              <div key={obj.id} className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${obj.position_x}%`, top: `${obj.position_y}%` }}>
                {/* Dot */}
                <div className="w-4 h-4 rounded-full bg-emerald-500 border-2 border-white shadow-lg" />
                {/* Label — always visible */}
                <div className={`absolute left-6 top-1/2 -translate-y-1/2 z-10 flex items-center gap-1.5 max-w-[150px] px-2.5 py-1.5 rounded-xl shadow-md ${
                  isDarkMode ? 'bg-gray-900/90 text-white' : 'bg-white/95 text-gray-800'
                }`} dir={isRtl ? 'rtl' : undefined}>
                  <span className="text-xs font-bold truncate">{label}</span>
                  {audioUrl && (
                    <button onClick={(e) => { e.stopPropagation(); playAudio(audioUrl); }}
                      className="p-0.5 rounded-full hover:bg-purple-500/20 transition-colors" title="Play audio">
                      <svg className="w-4 h-4 text-purple-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {scale > 1 && <p className={`text-center text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('scene_explorer_pinch_zoom')}</p>}

      {/* Game Buttons */}
      <div className={`grid ${getQuizSetting('match_game.enabled', true) !== false && getQuizSetting('correct_game.enabled', true) !== false ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
        {getQuizSetting('match_game.enabled', true) !== false && (
          <button onClick={startMatchGame}
            className="px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.98]">
            {gameSettings?.match_game?.name || 'Match the Label'}
          </button>
        )}
        {getQuizSetting('correct_game.enabled', true) !== false && (
          <button onClick={startCorrectGame}
            className="px-4 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.98]">
            {gameSettings?.correct_game?.name || 'Is This Correct?'}
          </button>
        )}
      </div>
      {/* Restart Scene — only if user has progress */}
      {scenes.find(s => s.id === selectedScene?.id)?.user_progress && (
        <button onClick={() => {
          const deps = scenes.filter(s => s.unlock_requires?.includes(selectedScene.id));
          setResetConfirm({ sceneId: selectedScene.id, sceneName: selectedScene.name, dependentScenes: deps });
        }}
          className={`w-full text-center px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-red-900/30 hover:text-red-400' : 'bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-500'
          }`}>
          Restart Scene
        </button>
      )}
    </div>
  );

  // ── Shared Game UI Components ──
  const renderGameHeader = (idx, total, score) => {
    const pctDone = total > 0 ? ((idx) / total) * 100 : 0;
    const radius = 18;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (pctDone / 100) * circumference;
    return (
      <>
        {/* Timer bar with label */}
        {timerActive && gameSettings?.ui?.show_timer_bar !== false && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 shrink-0">
              <svg className={`w-3.5 h-3.5 ${timerProgress > 30 ? (isDarkMode ? 'text-gray-400' : 'text-gray-500') : 'text-red-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className={`text-[10px] font-bold ${timerProgress > 30 ? (isDarkMode ? 'text-gray-500' : 'text-gray-400') : 'text-red-500'}`}>{t('scene_explorer_timer')}</span>
            </div>
            <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <div className="h-full rounded-full transition-all duration-100" style={{
                width: `${timerProgress}%`,
                background: timerProgress > 60 ? '#22c55e' : timerProgress > 30 ? '#eab308' : '#ef4444'
              }} />
            </div>
          </div>
        )}
        <div className="flex items-center justify-between">
          <button onClick={() => { stopTimer(); goBack(); }} className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          {/* Progress ring */}
          <div className="relative w-11 h-11">
            <svg className="w-11 h-11 -rotate-90" viewBox="0 0 44 44">
              <circle cx="22" cy="22" r={radius} fill="none" stroke={isDarkMode ? '#374151' : '#e5e7eb'} strokeWidth="3" />
              <circle cx="22" cy="22" r={radius} fill="none" stroke="#8b5cf6" strokeWidth="3"
                strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
                className="transition-all duration-500" />
            </svg>
            <span className={`absolute inset-0 flex items-center justify-center text-[10px] font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {idx + 1}/{total}
            </span>
          </div>
          {/* Streak badge */}
          {streak >= 2 && gameSettings?.ui?.show_streak !== false && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${
              streak >= 5 ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white animate-pulse' : 'bg-orange-500/20 text-orange-500'
            }`}>
              <span>🔥</span> x{streak}
            </div>
          )}
          <p className={`text-sm font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{score} pts</p>
        </div>
      </>
    );
  };

  const renderGameOverlays = () => (
    <>
      {/* Answer flash overlay */}
      {answerFlash && (
        <div className={`fixed inset-0 z-40 pointer-events-none ${
          answerFlash === 'correct' ? 'bg-emerald-500/20' : 'bg-red-500/20'
        }`} style={{ animation: 'fadeOut 0.4s forwards' }} />
      )}
      {/* Score popup */}
      {scorePopup && (
        <div className="fixed top-1/3 left-1/2 -translate-x-1/2 z-50 pointer-events-none text-2xl font-black text-emerald-500"
          style={{ animation: 'floatUp 0.8s forwards' }}>
          {scorePopup}
        </div>
      )}
      {/* Time's up popup */}
      {showTimesUp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 pointer-events-none">
          <div className={`px-6 py-4 rounded-2xl text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}
            style={{ animation: 'popIn 0.3s forwards' }}>
            <p className="text-3xl mb-1">⏰</p>
            <p className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('scene_explorer_times_up')}</p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('scene_explorer_moving_next')}</p>
          </div>
        </div>
      )}
    </>
  );

  // ── Game: Match the Label ──
  const renderMatchGame = () => {
    const currentObj = gameQueue[currentGameIdx];

    if (gameFinished) return renderGameResults();

    return (
      <div className="space-y-3 relative">
        {renderGameOverlays()}
        {renderGameHeader(currentGameIdx, gameQueue.length, gameScore)}

        {/* Scene with highlighted dot */}
        <div className={`${cardClass} p-2 overflow-hidden`}>
          <div className="relative">
            <img src={resolveImageUrl(selectedScene?.image_url)} alt="" className="w-full rounded-lg" draggable={false} />
            {sceneObjects.map(obj => (
              <div key={obj.id} className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${obj.position_x}%`, top: `${obj.position_y}%` }}>
                <div className={`rounded-full border-2 shadow-lg transition-all ${
                  highlightedDot === obj.id
                    ? 'w-7 h-7 bg-yellow-400 border-yellow-200 animate-bounce'
                    : 'w-3 h-3 bg-gray-400/50 border-white/50'
                }`}>
                  {highlightedDot === obj.id && <span className="flex items-center justify-center text-xs font-bold text-gray-900">?</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-center gap-2">
          <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {getQuizSetting('match_game.question_text', t('scene_explorer_q_what_is_this'))}
          </p>
          {currentObj?.audio_urls?.[displayLang] && (
            <button onClick={() => playAudio(currentObj.audio_urls[displayLang])}
              className="p-1.5 rounded-full hover:bg-purple-500/20 transition-colors"
              title="Listen">
              <svg className="w-5 h-5 text-purple-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
              </svg>
            </button>
          )}
        </div>

        {/* Options */}
        <div className="grid grid-cols-2 gap-2">
          {gameOptions.map(option => {
            const wasSelected = selectedAnswer === option.id;
            const gotItRight = wasSelected && option.isCorrect;
            const gotItWrong = wasSelected && !option.isCorrect;
            return (
              <button key={option.id} onClick={() => !selectedAnswer && handleMatchAnswer(option)}
                disabled={!!selectedAnswer}
                className={`p-3 rounded-xl text-sm font-semibold transition-all break-words ${
                  selectedAnswer
                    ? gotItRight
                      ? 'bg-emerald-500 text-white scale-105'
                      : gotItWrong
                        ? 'bg-red-500 text-white shake'
                        : isDarkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400'
                    : isDarkMode
                      ? 'bg-gray-800 hover:bg-gray-700 text-white active:scale-95'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800 active:scale-95'
                }`}>
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Game: Is This Correct? ──
  const renderCorrectGame = () => {
    if (gameFinished) return renderGameResults();

    return (
      <div className="space-y-3 relative">
        {renderGameOverlays()}
        {renderGameHeader(correctGameIdx, gameQueue.length, correctGameScore)}

        {/* Scene */}
        <div className={`${cardClass} p-2 overflow-hidden`}>
          <div className="relative">
            <img src={resolveImageUrl(selectedScene?.image_url)} alt="" className="w-full rounded-lg" draggable={false} />
            {sceneObjects.map(obj => (
              <div key={obj.id} className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${obj.position_x}%`, top: `${obj.position_y}%` }}>
                <div className={`rounded-full border-2 shadow-lg transition-all ${
                  highlightedDot === obj.id
                    ? 'w-7 h-7 bg-yellow-400 border-yellow-200 animate-bounce'
                    : 'w-3 h-3 bg-gray-400/50 border-white/50'
                }`} />
              </div>
            ))}
          </div>
        </div>

        {/* Question */}
        <div className={`${cardClass} p-4 text-center`}>
          <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{getQuizSetting('correct_game.question_text', t('scene_explorer_q_is_correct'))}</p>
          <div className="flex items-center justify-center gap-2">
            <p className={`text-2xl font-black break-words ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{correctGameLabel}</p>
            {correctGameIsRight && correctGameObj?.audio_urls?.[displayLang] && (
              <button onClick={() => playAudio(correctGameObj.audio_urls[displayLang])}
                className="p-1.5 rounded-full hover:bg-purple-500/20 transition-colors"
                title="Listen">
                <svg className="w-6 h-6 text-purple-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Yes / No buttons */}
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => !selectedAnswer && handleCorrectAnswer(true)}
            disabled={!!selectedAnswer}
            className={`p-4 rounded-xl text-lg font-bold transition-all ${
              selectedAnswer
                ? selectedAnswer === 'right' ? 'bg-emerald-500 text-white' : 'bg-red-500/20 text-red-400'
                : 'bg-emerald-600 hover:bg-emerald-700 text-white active:scale-95'
            }`}>
            {getQuizSetting('correct_game.yes_text', t('scene_explorer_yes'))}
          </button>
          <button onClick={() => !selectedAnswer && handleCorrectAnswer(false)}
            disabled={!!selectedAnswer}
            className={`p-4 rounded-xl text-lg font-bold transition-all ${
              selectedAnswer
                ? selectedAnswer === 'right' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500 text-white'
                : isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white active:scale-95' : 'bg-gray-200 hover:bg-gray-300 text-gray-800 active:scale-95'
            }`}>
            {getQuizSetting('correct_game.no_text', t('scene_explorer_no'))}
          </button>
        </div>
      </div>
    );
  };

  // ── Game Results ──
  const renderGameResults = () => {
    const total = gameQueue.length;
    const pct = total > 0 ? Math.round((gameScore / total) * 100) : 0;
    const rs = gameSettings?.results || {};
    const highT = rs.high?.threshold ?? 80;
    const midT = rs.mid?.threshold ?? 50;
    const resultEmoji = pct >= highT ? (rs.high?.emoji || '🎉') : pct >= midT ? (rs.mid?.emoji || '👍') : (rs.low?.emoji || '💪');
    const resultMsg = pct >= highT ? (rs.high?.message || 'Amazing!') : pct >= midT ? (rs.mid?.message || 'Good job!') : (rs.low?.message || 'Keep practicing!');
    const bonusPerfect = gameSettings?.bonus_perfect ?? 50;

    const ptsPerCorrect = gameSettings?.points_per_correct ?? 10;

    return (
      <div className="space-y-4 relative">
        {/* Celebration burst for 100% */}
        {pct === 100 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 overflow-hidden">
            {['🎉', '⭐', '🔥', '✨', '🏆', '💫'].map((e, i) => (
              <span key={i} className="absolute text-3xl" style={{
                animation: `confettiBurst 1.5s ${i * 0.15}s forwards`,
                transform: `rotate(${i * 60}deg) translateX(${20 + i * 15}px)`
              }}>{e}</span>
            ))}
          </div>
        )}

        <div className={`${cardClass} p-8 text-center`}>
          {/* Big animated emoji */}
          <p className="text-6xl mb-3" style={{ animation: pct === 100 ? 'celebSpin 0.6s ease-in-out' : undefined }}>{resultEmoji}</p>
          <p className={`text-3xl font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{gameScore} / {total}</p>
          <p className={`text-lg font-semibold mt-1 ${pct >= highT ? 'text-emerald-500' : pct >= midT ? 'text-yellow-500' : 'text-orange-500'}`}>
            {pct}% — {resultMsg}
          </p>

          {/* Streak display */}
          {bestStreak >= 2 && (
            <div className={`inline-flex items-center gap-1 mt-2 px-3 py-1 rounded-full text-xs font-bold ${
              isDarkMode ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-50 text-orange-600'
            }`}>
              🔥 Best streak: x{bestStreak}
            </div>
          )}

          {/* Points breakdown */}
          {scoreResult && (
            <div className={`mt-4 p-3 rounded-xl ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
              {scoreResult.is_new_best && (
                <p className="text-sm font-bold text-yellow-500 animate-pulse mb-2">{t('scene_explorer_new_best')}</p>
              )}
              <div className={`text-xs space-y-0.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <p>{gameScore} correct × {ptsPerCorrect} = <strong>{gameScore * ptsPerCorrect} pts</strong></p>
                {pct === 100 && <p>{t('scene_explorer_perfect_bonus')} <strong className="text-emerald-500">+{bonusPerfect} pts</strong></p>}
                <p className={`text-sm font-bold mt-1 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                  Total: +{scoreResult.points_earned} points
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => { setMode('view'); setGameFinished(false); }}
            className={`p-3 rounded-xl font-semibold text-sm ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
            Back to Scene
          </button>
          <button onClick={goBack}
            className="p-3 rounded-xl font-semibold text-sm bg-gradient-to-r from-purple-600 to-blue-600 hover:opacity-90 text-white">
            All Scenes
          </button>
        </div>
      </div>
    );
  };

  if (loading) return (
    <div className="max-w-4xl mx-auto p-4 text-center py-12">
      <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{t('amazigh_loading')}</p>
    </div>
  );

  return (
    <div className="p-4 md:p-6">
      {/* CSS Keyframes for game animations */}
      <style>{`
        @keyframes floatUp { 0% { opacity:1; transform:translate(-50%, 0) } 100% { opacity:0; transform:translate(-50%, -50px) } }
        @keyframes fadeOut { 0% { opacity:0.25 } 100% { opacity:0 } }
        @keyframes popIn { 0% { transform:scale(0.5); opacity:0 } 100% { transform:scale(1); opacity:1 } }
        @keyframes confettiBurst { 0% { transform:scale(0) rotate(0deg); opacity:1 } 100% { transform:scale(2) rotate(180deg); opacity:0 } }
        @keyframes celebSpin { 0% { transform:rotate(0deg) scale(1) } 50% { transform:rotate(15deg) scale(1.2) } 100% { transform:rotate(0deg) scale(1) } }
      `}</style>
      {mode === 'browse' && renderBrowse()}
      {mode !== 'browse' && (
        <div className="max-w-3xl mx-auto">
          {mode === 'view' && renderView()}
          {mode === 'game_match' && renderMatchGame()}
          {mode === 'game_correct' && renderCorrectGame()}
        </div>
      )}

      {/* How to Play Modal */}
      {showHowToPlay && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowHowToPlay(false)}>
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 w-full max-w-sm`} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>🎮 {t('scene_how_to_play')}</h3>
              <button onClick={() => setShowHowToPlay(false)} className={`p-1 rounded-lg ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className={`space-y-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {howToPlaySteps.map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                    isDarkMode ? 'bg-purple-900/60 text-purple-300' : 'bg-purple-100 text-purple-700'
                  }`}>{i + 1}</span>
                  <div>
                    <p className="font-semibold text-sm">{step.title}</p>
                    <p className="text-xs opacity-75">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setShowHowToPlay(false)}
              className="w-full mt-5 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold text-sm transition-all">
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* Locked Scene Modal */}
      {lockedScene && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setLockedScene(null)}>
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 w-full max-w-sm text-center`} onClick={e => e.stopPropagation()}>
            {/* Lock icon */}
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
              isDarkMode ? 'bg-orange-900/40' : 'bg-orange-50'
            }`}>
              <svg className={`w-8 h-8 ${isDarkMode ? 'text-orange-400' : 'text-orange-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>

            {/* Title */}
            <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Scene Locked
            </h3>

            {/* Explanation */}
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {lockedScene.unlock_required_names?.length > 0
                ? <>You need to complete <strong className={isDarkMode ? 'text-white' : 'text-gray-800'}>{lockedScene.unlock_required_names.join(', ')}</strong> with a perfect score before you can unlock <strong className={isDarkMode ? 'text-white' : 'text-gray-800'}>{lockedScene.name}</strong>.</>
                : <>This scene is not available yet. Complete other scenes first to unlock it.</>
              }
            </p>

            {/* Encouraging message */}
            <div className={`rounded-xl p-3 mb-5 ${isDarkMode ? 'bg-purple-900/30 border border-purple-700/40' : 'bg-purple-50 border border-purple-100'}`}>
              <p className={`text-xs ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                Keep learning! Every scene you finish brings you closer to unlocking new ones.
              </p>
            </div>

            {/* Required scenes list */}
            {lockedScene.unlock_required_names?.length > 0 && (
              <div className="mb-5">
                <p className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('scene_explorer_required_scenes')}</p>
                <div className="space-y-1.5">
                  {lockedScene.unlock_required_names.map((name, i) => {
                    const reqId = lockedScene.unlock_requires?.[i];
                    const reqScene = scenes.find(s => s.id === reqId);
                    const isCompleted = reqScene?.user_progress?.completed;
                    return (
                      <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                        isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'
                      }`}>
                        {isCompleted ? (
                          <svg className="w-4 h-4 text-emerald-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <div className={`w-4 h-4 rounded-full border-2 shrink-0 ${isDarkMode ? 'border-gray-500' : 'border-gray-300'}`} />
                        )}
                        <span className={`${isCompleted ? (isDarkMode ? 'text-emerald-400' : 'text-emerald-600') : (isDarkMode ? 'text-gray-300' : 'text-gray-600')}`}>
                          {name}
                        </span>
                        {isCompleted && <span className="text-[10px] text-emerald-500 font-bold ml-auto">DONE</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <button onClick={() => setLockedScene(null)}
              className="w-full px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold text-sm transition-all">
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {resetConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setResetConfirm(null)}>
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 w-full max-w-sm text-center`} onClick={e => e.stopPropagation()}>
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
              isDarkMode ? 'bg-red-900/40' : 'bg-red-50'
            }`}>
              <svg className={`w-8 h-8 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Restart Scene?
            </h3>
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Your progress for <strong className={isDarkMode ? 'text-white' : 'text-gray-800'}>{resetConfirm.sceneName}</strong> will be reset to 0%.
            </p>
            {resetConfirm.dependentScenes?.length > 0 && (
              <div className={`rounded-xl p-3 mb-4 text-left ${isDarkMode ? 'bg-orange-900/30 border border-orange-700/40' : 'bg-orange-50 border border-orange-100'}`}>
                <p className={`text-xs font-semibold mb-1.5 ${isDarkMode ? 'text-orange-300' : 'text-orange-600'}`}>
                  These scenes will lock again:
                </p>
                {resetConfirm.dependentScenes.map(s => (
                  <p key={s.id} className={`text-xs ${isDarkMode ? 'text-orange-200' : 'text-orange-700'}`}>
                    • {s.name}
                  </p>
                ))}
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => setResetConfirm(null)}
                className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                  isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}>
                Cancel
              </button>
              <button onClick={async () => {
                try {
                  const res = await fetch(`${API}/scene-explorer/scenes/${resetConfirm.sceneId}/my-progress`, { method: 'DELETE', headers });
                  if (res.ok) {
                    showNotification('Progress reset — scene is back to 0%', 'success');
                    setResetConfirm(null);
                    setMode('browse');
                    setSelectedScene(null);
                    setSceneObjects([]);
                    loadScenes();
                  }
                } catch (e) { showNotification('Error resetting progress', 'error'); }
              }}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold text-sm transition-all">
                Reset Progress
              </button>
            </div>
          </div>
        </div>
      )}

      {/* My Journey Modal */}
      {showJourney && (() => {
        const totalScenes = scenes.length;
        const completedScenes = scenes.filter(s => s.user_progress?.completed);
        const inProgressScenes = scenes.filter(s => s.user_progress && !s.user_progress.completed);
        const availableScenes = scenes.filter(s => !s.user_progress && (!s.is_locked || s.is_unlocked));
        const nextUp = availableScenes[0] || inProgressScenes[0];
        // Group scenes by category
        const cats = [...new Set(scenes.map(s => s.category || 'Other'))];

        return (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowJourney(false)}>
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col`} onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="p-5 pb-3 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{gameSettings?.ui?.journey_button_icon || '🗺️'}</span>
                  <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {gameSettings?.ui?.journey_button_text || 'My Journey'}
                  </h3>
                </div>
                <button onClick={() => setShowJourney(false)} className={`p-1 rounded-lg ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              {/* Stats row */}
              <div className="px-5 pb-3 shrink-0">
                <div className={`grid grid-cols-3 gap-2 p-3 rounded-xl ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  <div className="text-center">
                    <p className={`text-lg font-black ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{completedScenes.length}</p>
                    <p className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('scene_explorer_completed')}</p>
                  </div>
                  <div className="text-center">
                    <p className={`text-lg font-black ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>{totalPoints}</p>
                    <p className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('dashboard_points')}</p>
                  </div>
                  <div className="text-center">
                    <p className={`text-lg font-black ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>{totalScenes - completedScenes.length}</p>
                    <p className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('scene_explorer_remaining')}</p>
                  </div>
                </div>

                {/* Overall bar */}
                <div className="mt-3">
                  <div className={`w-full h-2.5 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all"
                      style={{ width: `${totalScenes > 0 ? Math.round((completedScenes.length / totalScenes) * 100) : 0}%` }} />
                  </div>
                  <p className={`text-[11px] mt-1 text-center font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {totalScenes > 0 ? Math.round((completedScenes.length / totalScenes) * 100) : 0}% complete
                  </p>
                </div>
              </div>

              {/* Next up */}
              {nextUp && (
                <div className="px-5 pb-3 shrink-0">
                  <div className={`p-3 rounded-xl border-2 border-dashed ${isDarkMode ? 'border-purple-500/40 bg-purple-900/20' : 'border-purple-200 bg-purple-50/50'}`}>
                    <p className={`text-[10px] font-bold mb-1 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>NEXT UP</p>
                    <p className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{nextUp.name}</p>
                    {nextUp.user_progress && (
                      <p className={`text-[10px] ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                        In progress — {Math.round(((nextUp.user_progress.best_score || 0) / (nextUp.object_count || 1)) * 100)}%
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Scene checklist by category — scrollable */}
              <div className="flex-1 overflow-y-auto px-5 pb-5">
                <div className="space-y-4">
                  {cats.map(cat => {
                    const catScenes = scenes.filter(s => (s.category || 'Other') === cat);
                    return (
                      <div key={cat}>
                        <p className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {categoryIcons[cat] || '📍'} {cat}
                        </p>
                        <div className="space-y-1">
                          {catScenes.map(scene => {
                            const isComplete = scene.user_progress?.completed;
                            const isLocked = scene.is_locked && !scene.is_unlocked;
                            const inProg = scene.user_progress && !scene.user_progress.completed;
                            const pct = scene.user_progress
                              ? Math.round(((scene.user_progress.best_score || 0) / (scene.object_count || 1)) * 100)
                              : 0;
                            return (
                              <div key={scene.id} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg ${
                                isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'
                              }`}>
                                {/* Status icon */}
                                {isComplete ? (
                                  <svg className="w-5 h-5 text-emerald-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                ) : isLocked ? (
                                  <svg className="w-5 h-5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                  </svg>
                                ) : inProg ? (
                                  <div className="w-5 h-5 rounded-full border-2 border-yellow-400 flex items-center justify-center shrink-0">
                                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                                  </div>
                                ) : (
                                  <div className={`w-5 h-5 rounded-full border-2 shrink-0 ${isDarkMode ? 'border-gray-500' : 'border-gray-300'}`} />
                                )}
                                {/* Scene info */}
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-semibold truncate ${
                                    isComplete ? (isDarkMode ? 'text-emerald-400' : 'text-emerald-600')
                                      : isLocked ? (isDarkMode ? 'text-gray-500' : 'text-gray-400')
                                      : (isDarkMode ? 'text-white' : 'text-gray-800')
                                  }`}>{scene.name}</p>
                                  {isLocked && scene.unlock_required_names?.length > 0 && (
                                    <p className={`text-[10px] truncate ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                      Complete {scene.unlock_required_names.join(', ')}
                                    </p>
                                  )}
                                  {inProg && <p className={`text-[10px] ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{pct}% done</p>}
                                </div>
                                {/* Right badge */}
                                {isComplete && <span className="text-[10px] font-bold text-emerald-500 shrink-0">100%</span>}
                                {inProg && <span className={`text-[10px] font-bold shrink-0 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{pct}%</span>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default SceneExplorer;
